import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { COLUMNS } from "./Columns";
import { Checkbox } from "../../../../components/common/Checkbox";
import privateAxios from "../../../../utils/axiosPrivate";
import DualPagination from "../../../common/DualPagination";
import { Link } from "react-router-dom";
import {
  showToast,
  formatDateWithDayjs,
  formatCurrency,
  formatCurrencyWithLocale,
  renderCheckIcon
} from "../../../../utils/utils";
import PropTypes from "prop-types";
import {
  showToastType,
  CURRENCY_FORMAT,
  PAGE_INDEX,
  PAGE_SIZE_25
} from "../../../../utils/constants/appConstants";
import {
  customers,
  ErrorText,
  noDataPresent,
  SearchText
} from "../../../../locales/en";
import "./customerlisttable.scss";
import CustomerListHeader from "./CustomerListHeader";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import {
  getClientStatusBulkUrl,
  getCustomersUrl
} from "../../../../utils/constants/apiEndPoints";
import { useColumnSort } from "../../../../hooks/useColumnSort";
import SortableHeaderCell from "../../../common/SortableHeaderCell";
import ErrorBoundary from "../../../error-boundary";
import ListViewFallback from "../../../error-boundary/fallback/ListViewFallback";
import {
  CLIENT_DETAILS,
  CLIENT_DETAILS_WITH_FROM
} from "../../../../route/paths";
import useResetExpandedOnPagination from "../../../../hooks/useResetExpandedOnPagination";

const SelectionHeader = ({ getToggleAllRowsSelectedProps }) => (
  <Checkbox {...getToggleAllRowsSelectedProps()} className="headercheckBox" />
);
SelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func.isRequired
};

const SelectionCellWrapper = ({ row, setID, setName }) => {
  const handleCheckboxClick = (e) => {
    setID((prev) =>
      e.target.checked
        ? [...prev, row.original.id]
        : prev.filter((id) => id !== row.original.id)
    );
    setName(() => (e.target.checked ? row.original.displayName : ""));
  };

  return (
    <Checkbox
      {...row.getToggleRowSelectedProps()}
      onClick={handleCheckboxClick}
      className="bodycheckBox"
    />
  );
};
SelectionCellWrapper.propTypes = {
  row: PropTypes.object.isRequired,
  setID: PropTypes.func.isRequired,
  setName: PropTypes.func.isRequired
};

const CustomerListTable = () => {
  const baseColumns = useMemo(() => COLUMNS, []);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [loading, setLoading] = useState(false);
  const [statusSort, setStatusSort] = useState("");
  const [userSortType, setUserSortType] = useState("");
  const [getID, setID] = useState([]);
  const [getName, setName] = useState("");
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const initialState = {
    displayName: null,
    fullName: null,
    signupSource: null
  };

  const { columnOrders, handleSort } = useColumnSort(initialState);
  const sortableColumns = Object.keys(initialState);

  const loadCustomerData = useCallback(
    async (pageNumber, size) => {
      try {
        setLoading(true);
        const response = await privateAxios.get(getCustomersUrl, {
          params: {
            page: pageNumber,
            size,
            keyword: searchQuery,
            customer: columnOrders.displayName,
            user: columnOrders.fullName,
            customerType: columnOrders.signupSource,
            statusFilter: statusSort,
            userType: userSortType
          }
        });
        setData(response.data.data || []);
        setTotalPages(response.data.pageInfo?.totalPages || 0);
      } catch (error) {
        showToast(error?.response?.data?.message, showToastType.error);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, statusSort, userSortType, JSON.stringify(columnOrders)]
  );

  const SelectionCell = useCallback(
    ({ row }) => (
      <SelectionCellWrapper row={row} setID={setID} setName={setName} />
    ),
    [setID, setName]
  );

  const tableColumns = useMemo(() => {
    if (data.length > 0) {
      return [
        {
          id: "selection",
          Header: SelectionHeader,
          Cell: SelectionCell
        },
        ...baseColumns
      ];
    }
    return baseColumns;
  }, [data, baseColumns, SelectionCell]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    selectedFlatRows
  } = useTable(
    {
      columns: tableColumns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (!value.trim()) setSearchQuery("");
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        gotoPage(0);
        setSearchQuery(tempSearchQuery.trim());
      }
    },
    [tempSearchQuery]
  );

  const handleStatusSort = useCallback(
    (e) => {
      setStatusSort(e.target.value);
      gotoPage(0);
    },
    [gotoPage]
  );
  const handleUserTypeSort = useCallback(
    (e) => {
      setUserSortType(e.target.value);
      gotoPage(0);
    },
    [gotoPage]
  );

  const [grpStatus, setGrpStatus] = useState("");
  const handleGroupStatusChange = useCallback(
    async (event) => {
      const { value } = event.target;
      setGrpStatus(value);
      try {
        const datatoSend = selectedFlatRows.map((row) => row.original.id);
        const response = await privateAxios.put(
          getClientStatusBulkUrl,
          datatoSend,
          {
            params: { newUserStatusId: value }
          }
        );
        if (response.status === 200) {
          showToast(response.data.message, showToastType.success);
          loadCustomerData(pageIndex, pageSize);
          setGrpStatus("");
        }
      } catch (error) {
        showToast(
          error?.response?.data?.message || ErrorText,
          showToastType.error
        );
      }
    },
    [selectedFlatRows, loadCustomerData, pageIndex, pageSize]
  );

  const [expandedRows, setExpandedRows] = useState([]);
  const toggleRow = useCallback(
    (rowId) =>
      setExpandedRows((prev) =>
        prev.includes(rowId)
          ? prev.filter((id) => id !== rowId)
          : [...prev, rowId]
      ),
    []
  );
  const isRowExpanded = useCallback(
    (rowId) => expandedRows.includes(rowId),
    [expandedRows]
  );

  const [showTopHideFilter, setShowTopHideFilter] = useState(false);
  const [refreshData, setRefreshData] = useState(false);

  const handleRefreshData = useCallback((value) => {
    setRefreshData(value);
    setTimeout(() => setRefreshData(false), 500);
  }, []);

  useEffect(() => {
    loadCustomerData(pageIndex, pageSize);
  }, [loadCustomerData, pageIndex, pageSize, refreshData]);

  useEffect(() => {
    setShowTopHideFilter(selectedFlatRows.length > 0);
  }, [selectedFlatRows]);

  useResetExpandedOnPagination(
    () => setExpandedRows([]),
    [pageIndex, pageSize, statusSort, searchQuery, userSortType]
  );

  return (
    <div className="customerlist-table-wrap">
      <CustomerListHeader
        getID={getID}
        getName={getName}
        handleRefreshData={handleRefreshData}
        handleUserTypeSort={handleUserTypeSort}
        userSortType={userSortType}
        handleStatusSort={handleStatusSort}
        statusSort={statusSort}
        tempSearchQuery={tempSearchQuery}
        handleSearchChange={handleSearchChange}
        handleKeyDown={handleKeyDown}
        SearchText={SearchText}
        showTopHideFilter={showTopHideFilter}
        grpStatus={grpStatus}
        handleGroupStatusChange={handleGroupStatusChange}
        setGrpStatus={setGrpStatus}
        disabled={loading}
        data={data}
      />
      <ErrorBoundary fallback={<ListViewFallback />}>
        <table className="mt-2 mb-3 customerlist-table" {...getTableProps()}>
          <thead ref={headerRef}>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr className="parentrow" {...rest} key={key}>
                  <th />
                  {headerGroup.headers.map((column) => {
                    const isSortable = sortableColumns.includes(column.id);
                    return (
                      <SortableHeaderCell
                        key={column.id}
                        column={column}
                        isSortable={isSortable}
                        columnOrders={columnOrders}
                        handleSort={handleSort}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </thead>

          <tbody {...getTableBodyProps()}>
            {loading && (
              <TableSkeleton rows={10} columns={visibleColumnCount} />
            )}
            {!loading && page.length === 0 && (
              <tr>
                <td colSpan={baseColumns.length + 2} className="text-center">
                  {noDataPresent}
                </td>
              </tr>
            )}
            {!loading &&
              page.length > 0 &&
              page.map((row) => {
                prepareRow(row);
                const isExpanded = isRowExpanded(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <tr className="parentrow" {...row.getRowProps()}>
                      <td className="expand-td">
                        <span
                          className="expandbtn cursor-pointer"
                          onClick={() => toggleRow(row.id)}
                        >
                          {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                        </span>
                      </td>

                      {row.cells.map((cell) => {
                        let cellContent = cell.render("Cell");

                        switch (cell.column.id) {
                          case "isAccountingSoftwareConnected":
                            cellContent = renderCheckIcon(cell.value);
                            break;
                          case "hasSubmittedInvoice":
                            cellContent = renderCheckIcon(cell.value);
                            break;
                          case "displayName":
                            cellContent = (
                              <Link to={CLIENT_DETAILS(row.original.id)}>
                                {`${row.original.displayName || row.original.fullName}`}
                              </Link>
                            );
                            break;
                          case "fullName":
                            cellContent = (
                              <Link
                                to={CLIENT_DETAILS_WITH_FROM(row.original.id)}
                              >
                                {`${row.original.fullName}`}
                              </Link>
                            );
                            break;
                          case "signupSource":
                            cellContent =
                              cell.value === customers?.regular
                                ? customers?.customer
                                : cell.value || customers?.regular;
                            break;
                          case "lastLoginAt":
                            cellContent = (
                              <>
                                {formatDateWithDayjs(cell.value, "DD-MMM-YYYY")}
                              </>
                            );
                            break;
                          case "creditLimit":
                            cellContent = (
                              <>
                                {CURRENCY_FORMAT}
                                {formatCurrency(cell.value)}
                              </>
                            );
                            break;
                          case "totalFundedInvoices":
                            cellContent = (
                              <>
                                {CURRENCY_FORMAT}
                                {formatCurrencyWithLocale(cell.value)}
                              </>
                            );
                            break;
                          case "totalFundedDue":
                            cellContent = (
                              <>
                                {CURRENCY_FORMAT}
                                {formatCurrencyWithLocale(cell.value)}
                              </>
                            );
                            break;
                          default:
                            break;
                        }

                        return (
                          <td
                            key={cell.getCellProps().key}
                            {...cell.getCellProps()}
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>

                    {isExpanded && (
                      <tr className="childrow colexpand">
                        <td colSpan={baseColumns.length + 2}>
                          <div className="tdbody">
                            {baseColumns.map((column) => {
                              let content = row.original[column.accessor] || "";
                              switch (column.accessor) {
                                case "hasSubmittedInvoice":
                                  content = renderCheckIcon(content);
                                  break;
                                case "isAccountingSoftwareConnected":
                                  content = renderCheckIcon(content);
                                  break;
                                case "displayName":
                                  content = (
                                    <Link
                                      title={customers.goTouser}
                                      to={CLIENT_DETAILS(row.original.id)}
                                    >
                                      {`${row.original.displayName ?? row.original.fullName}`}
                                    </Link>
                                  );
                                  break;
                                case "lastLoginAt":
                                  content = formatDateWithDayjs(
                                    content,
                                    "DD-MMM-YYYY"
                                  );
                                  break;
                                case "creditLimit":
                                  content = (
                                    <>
                                      {CURRENCY_FORMAT}
                                      {formatCurrency(content)}
                                    </>
                                  );
                                  break;
                                case "totalFundedInvoices":
                                  content = (
                                    <>
                                      {CURRENCY_FORMAT}
                                      {formatCurrencyWithLocale(content)}
                                    </>
                                  );
                                  break;
                                case "totalFundedDue":
                                  content = (
                                    <>
                                      {CURRENCY_FORMAT}
                                      {formatCurrencyWithLocale(content)}
                                    </>
                                  );
                                  break;
                                case "signupSource":
                                  content =
                                    content === customers?.regular
                                      ? customers?.customer
                                      : content || customers?.regular;
                                  break;
                                default:
                                  break;
                              }

                              return (
                                <span
                                  className="mb-2 expandheadtext expandheadtextHide"
                                  key={column.accessor}
                                >
                                  <strong>{column.Header} :</strong> {content}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </ErrorBoundary>
      {data && data.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
        />
      )}
    </div>
  );
};

export default CustomerListTable;
